import React, { useContext, useState, ReactNode } from 'react';
import { Repository, SetupProgress, AnalysisData } from '../types';
import { AppContext, type AppContextValue, type DemoState } from './appContextInstance';

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [selectedRepository, setSelectedRepository] = useState<Repository | null>(null);
  const [setupProgress, setSetupProgress] = useState<SetupProgress>({
    currentStep: '',
    progress: 0,
    isComplete: false,
  });
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [isDemoMode, setDemoMode] = useState<boolean>(
    () => sessionStorage.getItem('demoMode') === 'true',
  );
  const [flowType, setFlowType] = useState<'repository' | 'upload' | 'demo' | null>(
    () => (sessionStorage.getItem('flowType') as 'repository' | 'upload' | 'demo') || null,
  );
  const [demoState, setDemoState] = useState<DemoState>({
    isActive: false,
    preventNetworkRequests: false,
    mockDataLoaded: false,
    demoRepository: null,
  });

  const resetDemoState = () => {
    setDemoState({
      isActive: false,
      preventNetworkRequests: false,
      mockDataLoaded: false,
      demoRepository: null,
    });
    setDemoMode(false);
    sessionStorage.removeItem('demoMode');
    sessionStorage.removeItem('flowType');
    sessionStorage.removeItem('currentContainerId');
    setFlowType(null);
    setSelectedRepository(null);
  };

  const ensureDemoMode = () => {
    if (isDemoMode || flowType === 'demo') {
      setDemoState((prev) => ({
        ...prev,
        isActive: true,
        preventNetworkRequests: true,
      }));

      // Create demo repository if not already set
      if (!selectedRepository || selectedRepository.id !== 'demo-project') {
        const demoRepository: Repository = {
          id: 'demo-project',
          name: 'demo-project',
          description: 'Sample TypeScript project for demonstration',
          updatedAt: new Date().toISOString(),
          language: 'TypeScript',
          private: true,
          isPrivate: true,
          stars: 0,
          forks: 0,
          url: 'https://github.com/demo/demo-project',
          fullName: 'demo/demo-project',
          size: 1024,
          lastUpdated: new Date(),
          source: 'git',
        };

        setSelectedRepository(demoRepository);
        setDemoState((prev) => ({
          ...prev,
          demoRepository,
        }));
      }
    }
  };

  // Enhanced setDemoMode that updates demo state and persists to sessionStorage
  const enhancedSetDemoMode = (isDemo: boolean) => {
    setDemoMode(isDemo);

    if (isDemo) {
      sessionStorage.setItem('demoMode', 'true');
      sessionStorage.setItem('flowType', 'demo');
      setFlowType('demo');
      setDemoState((prev) => ({
        ...prev,
        isActive: true,
        preventNetworkRequests: true,
      }));
    } else {
      resetDemoState();
    }
  };

  const value: AppContextValue = {
    selectedRepository,
    setSelectedRepository,
    setupProgress,
    setSetupProgress,
    analysisData,
    setAnalysisData,
    isDemoMode,
    setDemoMode: enhancedSetDemoMode,
    flowType,
    setFlowType,
    demoState,
    resetDemoState,
    ensureDemoMode,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
